import { Component, EventEmitter, Input, OnInit, Output, SimpleChange } from '@angular/core';
import { ToastrManager } from '@niceltd/cxone-components/toastr';
import { cloneDeep } from 'lodash';
import { Subscription } from 'rxjs';
import { AttributeSelectorService } from 'src/app/reusables/attribute-selector/attribute-selector.service';
import { GroupSelectorService } from 'src/app/reusables/group-selector/group-selector.service';
import { GroupSelectorPostObject } from 'src/app/reusables/models/group-selector.model';
import { BaseGroupType } from 'src/app/reusables/models/group-type.model';
import { DashboardManager } from 'src/shared/models/dashboard-manager';
import { DashboardModuleResponsiveInstanceForGridster } from 'src/shared/models/module-info.model';
import { ModuleManager } from 'src/shared/models/module-manager';
import SlideoutSettingsManager from 'src/shared/models/slideout-settings-manager';
import { ClearViewUtils } from '../../../../shared/utils';
import { CVLocalizationService } from '../../../reusables/services/cv-localization.service';
import { DashboardService } from '../../dashboard.service';
import { ModuleContainerService } from '../../module-container/module-container.service';
import { CxoneDashboardFilterService } from './cxone-dashboard-filter.service';
import { NavigationService } from 'src/app/navigation/navigation.service';

@Component({
    selector: 'cv-cxone-dashboard-filter',
    templateUrl: './cxone-dashboard-filter.component.html',
    styleUrls: ['./cxone-dashboard-filter.component.scss']
})
export class CxoneDashboardFilterComponent implements OnInit {
    @Input() modules: DashboardModuleResponsiveInstanceForGridster[] = [];
    @Input() selectedFilters = { isValid: true, selection: [] };
    @Input() applyDashboardFiltersReversion = new EventEmitter<any>();
    @Output() applySelectedFilters = new EventEmitter<any>();
    @Output() updateFilterText = new EventEmitter<any>();
    @Output() isApplyButtonEnabled = new EventEmitter<any>();

    get dashboardKey() { return this.dashboardService?.activeDashboard?.key; }

    initialized = false;
    isApplied = false;
    applyingGroups = false;
    applyingAttrs = false;
    showToast = false;
    toastIsError = false;

    dashboardManager: DashboardManager;
    settingsManager: SlideoutSettingsManager;
    empSelectorDTO: any;
    recSelectorDTO: any;
    showErrorMessage = false;
    applyButtonEnabled = false;

    filterSelections: SimpleChange = {
        previousValue: undefined,
        currentValue: this.selectedFilters,
        firstChange: false,
        isFirstChange: () => false,
    };

    initialSelection: any;

    //emp filters
    filterTeams = "" as any;
    filterAgents = "" as any;

    // rec filters
    filterCampaigns = "" as any;
    filterSkills = "" as any;
    filterPOCs = "" as any;

    //Static QM filters
    filterDateParadigm = "" as any;
    filterOnTheFlyEvals = "" as any;
    filterCoachingSessionStatuses = "" as any;

    // attr filters -- add back when new attribute flow is introduced
    //filterMediaType = "" as any;
    //filterChannels = "" as any;
    //filterDirectionType = "" as any;

    //Dynamic QM filters
    filterForms = "" as any;
    filterGroups = "" as any;
    filterEvaluators = "" as any;

    private subscriptions: Subscription[] = [];

    constructor(
        private toasterManager: ToastrManager,
        private localizationService: CVLocalizationService,
        private dashboardFilterService: CxoneDashboardFilterService,
        private dashboardService: DashboardService,
        private moduleContainerService: ModuleContainerService,
        private attributeSelectorService: AttributeSelectorService,
        private groupSelectorService: GroupSelectorService,
        private navigationService: NavigationService,
    ) { }

    async ngOnInit() {
        try {
            const tasks = [
                this.localizationService.GetSection("Dashboard_Filter"),
                this.loadData(),
            ];
            await Promise.all(tasks);
            this.initializeFilterConfigs();
            this.initFilters();
            this.settingsManager = new SlideoutSettingsManager(this.attributeSelectorService);
            this.dashboardManager = this.dashboardService.dashboardManagers.get(this.dashboardService.activeDashboard.key);
        } catch (error) {
            console.error(error);
        }

        this.subscriptions.push(this.applyDashboardFiltersReversion.subscribe(() => {
            this.revertSavedFilters();
        }));
    }

    ngOnDestroy() {
        this.subscriptions?.forEach(s => s?.unsubscribe());
    }

    initializeFilterConfigs() {
        //emp filters
        this.filterTeams = this.createFilterConfig("filterTeams", "select", "Teams");
        this.filterAgents = this.createFilterConfig("filterAgents", "select", "Agents");

        // rec filters
        this.filterCampaigns = this.createFilterConfig("filterCampaigns", "select", "Campaigns");
        this.filterSkills = this.createFilterConfig("filterSkills", "select", "Skills");
        this.filterPOCs = this.createFilterConfig("filterPOCs", "select", "Points of Contact");

        //Static QM filters
        this.filterDateParadigm = this.createFilterConfig("filterDateParadigm", "select", "Date Paradigm", false);
        this.filterOnTheFlyEvals = this.createFilterConfig("filterOnTheFlyEvals", "select", "On-The-Fly Evaluations", false);
        this.filterCoachingSessionStatuses = this.createFilterConfig("filterCoachingSessionStatuses", "select", "Coaching Session Status");

        //Dynamic QM filters
        this.filterForms = this.createFilterConfig("filterForms", "select", "Forms");
        this.filterGroups = this.createFilterConfig("filterGroups", "select", "Groups");
        this.filterEvaluators = this.createFilterConfig("filterEvaluators", "select", "Evaluator");

        // attr filters -- add back when new attribute flow is introduced
        //filterMediaType  = this.createFilterConfig("filterMediaType", "select", "Media Type");
        //filterChannels = this.createFilterConfig("filterChannels", "select", "Channels");
        //filterDirectionType = this.createFilterConfig("filterDirectionType", "select", "Direction Type");
    }

    async loadData() {
        try {
            await this.dashboardFilterService.loadData();
        } catch (error) {
            console.error(error);
        };
    }
    
    createFilterConfig(filterId, filterType, filterName, isMulti = true) {
        filterName = this.localizationService.getCachedText("Dashboard_Filter", this.standardizeLocalizationKey(filterName), filterName);
        return {
            filterId,
            filterType,
            filterName,
            readOnly: false,
            filterConfig: {
                isMultiple: isMulti,
                searchEnabled: true,
                optionsLabel: "GroupName",
                optionsValue: "GroupID",
                dateFormat: null,
                options: null,
            },
        };
    }

    initFilters() {
        this.filterCampaigns.filterConfig.options = this.dashboardFilterService.campaigns;
        this.filterAgents.filterConfig.options = this.dashboardFilterService.agents;
        this.filterSkills.filterConfig.options = this.dashboardFilterService.skills;
        this.filterPOCs.filterConfig.options = this.dashboardFilterService.POCs;
        //this.filterMediaType.filterConfig.options = this.dashboardFilterService.mediaTypes;
        //this.filterChannels.filterConfig.options = this.dashboardFilterService.channels;
        //this.filterDirectionType.filterConfig.options = this.dashboardFilterService.directionTypes;
        this.filterTeams.filterConfig.options = this.dashboardFilterService.teams;
        this.filterDateParadigm.filterConfig.options = this.dashboardFilterService.dateParadigm;
        this.filterOnTheFlyEvals.filterConfig.options = this.dashboardFilterService.onTheFlyEvals;
        this.filterCoachingSessionStatuses.filterConfig.options = this.dashboardFilterService.coachingSessionStatuses;
        this.filterForms.filterConfig.options = this.dashboardFilterService.forms;
        this.filterGroups.filterConfig.options = this.dashboardFilterService.groups;
        this.filterEvaluators.filterConfig.options = this.dashboardFilterService.evaluators;

        this.selectedFilters.selection.forEach(filter => {
            if (this.getSelectedFilterValue(filter.filterId) == null) return;
            // check for all selected
            // the sol component does not use [0] for all selected, so select options up to 250 values
            if (this.getSelectedFilterValue(filter.filterId).length == 1 && this.getSelectedFilterValue(filter.filterId)[0] == 0) {
                switch (filter.filterId) {
                    case "filterTeams":
                        filter.value = this.filterTeams.filterConfig.options.map(option => option.GroupID).slice(0, 250);
                        break;
                    case "filterAgents":
                        filter.value = this.filterAgents.filterConfig.options.map(option => option.GroupID).slice(0, 250);
                        break;
                    case "filterCampaigns":
                        filter.value = this.filterCampaigns.filterConfig.options.map(option => option.GroupID).slice(0, 250);
                        break;
                    case "filterSkills":
                        filter.value = this.filterSkills.filterConfig.options.map(option => option.GroupID).slice(0, 250);
                        break;
                    case "filterPOCs":
                        filter.value = this.filterPOCs.filterConfig.options.map(option => option.GroupID).slice(0, 250);
                        break;
                    case "filterDateParadigm":
                        filter.value = this.filterDateParadigm.filterConfig.options.map(option => option.GroupID).slice(0, 250);
                        break;
                    case "filterOnTheFlyEvals":
                        filter.value = this.filterOnTheFlyEvals.filterConfig.options.map(option => option.GroupID).slice(0, 250);
                        break
                    case "filterCoachingSessionStatuses":
                        filter.value = this.filterCoachingSessionStatuses.filterConfig.options.map(option => option.GroupID).slice(0, 250);
                        break
                    case "filterForms":
                        filter.value = this.filterForms.filterConfig.options.map(option => option.GroupID).slice(0, 250);
                        break;
                    case "filterGroups":
                        filter.value = this.filterGroups.filterConfig.options.map(option => option.GroupID).slice(0, 250);
                        break;
                    case "filterEvaluators":
                        filter.value = this.filterEvaluators.filterConfig.options.map(option => option.GroupID).slice(0, 250);
                        break;
                    default:
                        return;
                }
            }
        });
        this.filterSelections.currentValue = cloneDeep(this.selectedFilters);
        this.initialSelection = cloneDeep(this.selectedFilters.selection);
        this.initialized = true;
    }

    async onSelectionChange(selection: any) {
        const isSelectionValid = selection.isValid;

        this.filterSelections.previousValue = cloneDeep(this.filterSelections.currentValue);
        this.filterSelections.currentValue = cloneDeep(selection);
        this.selectedFilters = cloneDeep(this.filterSelections.currentValue);

        if (this.filterSelections.currentValue.selection.length < this.filterSelections.previousValue.selection.length) {
            await this.filterRemoved();
        }
        else{
           this.applyButtonEnabled = isSelectionValid; 
        }

        this.isApplyButtonEnabled.emit(this.applyButtonEnabled);
    }

    getSelectedFilterValue(id: string): number[] | string[] {
        let index = this.selectedFilters.selection.findIndex(filter => filter.filterId === id);
        let isSingleSelect = false;
        if (this.selectedFilters.selection.length < 1 || index < 0) return null;
        else if (index > -1 && this.selectedFilters.selection[index]?.value?.length > 0) {
            let maxLength = 250; // max allowed by sol component
            switch (id) {
                case "filterTeams":
                    maxLength = Math.min(this.filterTeams.filterConfig.options.length, 250);
                    isSingleSelect = false;
                    break;
                case "filterAgents":
                    maxLength = Math.min(this.filterAgents.filterConfig.options.length, 250);
                    isSingleSelect = false;
                    break;
                case "filterCampaigns":
                    maxLength = Math.min(this.filterCampaigns.filterConfig.options.length, 250);
                    isSingleSelect = false;
                    break;
                case "filterSkills":
                    maxLength = Math.min(this.filterSkills.filterConfig.options.length, 250);
                    isSingleSelect = false;
                    break;
                case "filterPOCs":
                    maxLength = Math.min(this.filterPOCs.filterConfig.options.length, 250);
                    isSingleSelect = false;
                    break;
                case "filterDateParadigm":
                    maxLength = Math.min(this.filterDateParadigm.filterConfig.options.length, 250);
                    isSingleSelect = true;
                    break;
                case "filterOnTheFlyEvals":
                    maxLength = Math.min(this.filterOnTheFlyEvals.filterConfig.options.length, 250);
                    isSingleSelect = true;
                    break;
                case "filterCoachingSessionStatuses":
                    maxLength = Math.min(this.filterCoachingSessionStatuses.filterConfig.options.length, 250);
                    isSingleSelect = false;
                    break;
                case "filterForms":
                    maxLength = Math.min(this.filterForms.filterConfig.options.length, 250);
                    isSingleSelect = false;
                    break;
                case "filterGroups":
                    maxLength = Math.min(this.filterGroups.filterConfig.options.length, 250);
                    isSingleSelect = false;
                    break;
                case "filterEvaluators":
                    maxLength = Math.min(this.filterEvaluators.filterConfig.options.length, 250);
                    isSingleSelect = false;
                    break;
                default:
                    return null;
            }
            let returnValue = [0]; // [0] is "all" in the backend
            if (!isSingleSelect)
                returnValue = this.selectedFilters.selection[index].value.length >= maxLength ? [0] : cloneDeep(this.selectedFilters.selection[index].value);
            else
                returnValue = this.selectedFilters.selection[index].value == null ? null : [cloneDeep(this.selectedFilters.selection[index].value)];
            return returnValue;
        }
        else return null;
    }

    async applyFilter() {
        await this.applySettings();
        var selectedFilters = {
            isValid: true,
            selection: []
        };
        this.selectedFilters.selection.forEach(filter => {
            selectedFilters.selection.push({
                filterId: filter.filterId,
                value: this.getSelectedFilterValue(filter.filterId)
            });
        })
        this.applySelectedFilters.emit(selectedFilters);
        this.toasterManager.showToaster("success", "Filter saved successfully");
        this.isApplied = true;
        this.applyButtonEnabled = false;
        this.initialSelection = cloneDeep(selectedFilters.selection);
        this.isApplyButtonEnabled.emit(this.applyButtonEnabled);
    }

    public areFiltersSelected(): boolean {
        return this.selectedFilters?.selection?.length > 0;
    }


    public async applySettings() {
        this.navigationService.applyingFilters = true;
        const moduleKeys = this.dashboardManager.getModuleKeys();
        const promises: PromiseLike<any>[] = [];
        for (const key of moduleKeys) {
            const moduleManager = this.dashboardManager.getModuleManager(key);
            promises.push(this.saveGroups(moduleManager));
            promises.push(this.saveFilterables(moduleManager));
            //promises.push(this.saveAttributes(moduleManager)); // TODO - add back when new attribute flow is introduced
            await Promise.all(promises);
            await this.applyFilterOnModule(moduleManager);
            moduleManager.loadSettings(false);
        }
        this.navigationService.applyingFilters = false;
    }

    async saveGroups(moduleManager: ModuleManager) {
        if (!moduleManager?.moduleSettings?.empSelectorDTO && !moduleManager?.moduleSettings?.recSelectorDTO)
            return;

        const pageName = `${moduleManager.getModuleKey()}_temp`;
        const promises: PromiseLike<any>[] = [];
        
        let empSaveObject: GroupSelectorPostObject = { };
        if (this.getSelectedFilterValue("filterAgents"))
            empSaveObject.selectedGroup1Ids = this.getSelectedFilterValue("filterAgents") as number[];
        if (this.getSelectedFilterValue("filterTeams"))
            empSaveObject.selectedGroup2Ids = this.getSelectedFilterValue("filterTeams") as number[];

        let recSaveObject: GroupSelectorPostObject = { };
        if (this.getSelectedFilterValue("filterPOCs"))
            recSaveObject.selectedGroup1Ids = this.getSelectedFilterValue("filterPOCs") as number[];
        if (this.getSelectedFilterValue("filterSkills"))
            recSaveObject.selectedGroup2Ids = this.getSelectedFilterValue("filterSkills") as number[];
        if (this.getSelectedFilterValue("filterCampaigns"))
            recSaveObject.selectedGroup3Ids = this.getSelectedFilterValue("filterCampaigns") as number[];

        if (empSaveObject.selectedGroup1Ids || empSaveObject.selectedGroup2Ids)
            promises.push(this.groupSelectorService.saveGroupSelector(pageName, BaseGroupType.Employee, empSaveObject));

        if (recSaveObject.selectedGroup1Ids || recSaveObject.selectedGroup2Ids || recSaveObject.selectedGroup3Ids)
            promises.push(this.groupSelectorService.saveGroupSelector(pageName, BaseGroupType.Recording, recSaveObject));

        await Promise.all(promises);
    }

    async saveFilterables(moduleManager: ModuleManager) {
        const pageName = moduleManager.getModuleKey();

        if (this.getSelectedFilterValue("filterDateParadigm") && (moduleManager?.moduleSettings?.hasOwnProperty('dateParadigmName') || moduleManager?.loadedStateValues?.hasOwnProperty('dateParadigmName'))) {
            const settingsToSave = {};
            let dateParadigmName = moduleManager?.loadedStateValues?.dateParadigmName ?? null;
            dateParadigmName = this.getSelectedFilterValue("filterDateParadigm")[0];
            settingsToSave["dateParadigmName"] = dateParadigmName;
            moduleManager.loadedStateValues.dateParadigmName = settingsToSave["dateParadigmName"];
            await moduleManager.container.moduleContainerService.saveModuleStates(pageName, settingsToSave);
        }

        let filterablesSaveObject: Record<string, number[] | string[]> = {};
        if (moduleManager?.moduleSettings?.hasOwnProperty('reportFilterableFields') || moduleManager?.loadedStateValues?.hasOwnProperty('reportFilterableFields')) {
            if (this.getSelectedFilterValue("filterOnTheFlyEvals"))
                filterablesSaveObject["ASSIGNER_USER_ID"] = this.getSelectedFilterValue("filterOnTheFlyEvals");
            if (this.getSelectedFilterValue("filterCoachingSessionStatuses"))
                filterablesSaveObject["COACHING_SESSION_STATUS"] = this.getSelectedFilterValue("filterCoachingSessionStatuses");
            if (this.getSelectedFilterValue("filterForms"))
                filterablesSaveObject["FORM_KEY"] = this.getSelectedFilterValue("filterForms");
            if (this.getSelectedFilterValue("filterGroups"))
                filterablesSaveObject["GROUP_ID"] = this.getSelectedFilterValue("filterGroups");
            if (this.getSelectedFilterValue("filterEvaluators"))
                filterablesSaveObject["EVALUATOR_USER_ID"] = this.getSelectedFilterValue("filterEvaluators");

            if (Object.keys(filterablesSaveObject).length > 0) {
                const settingsToSave = {};
                let reportFilterableFields = moduleManager?.loadedStateValues?.reportFilterableFields ? JSON.parse(moduleManager?.loadedStateValues?.reportFilterableFields) : { Values: {} as any };;
                Object.entries(filterablesSaveObject).forEach(async ([filterableKey, value]) => {
                    reportFilterableFields.Values[filterableKey] = value;
                    settingsToSave["reportFilterableFields"] = JSON.stringify(reportFilterableFields);
                });
                moduleManager.loadedStateValues.reportFilterableFields = settingsToSave["reportFilterableFields"];
                moduleManager.moduleSettings.reportFilterableFields = reportFilterableFields;
                await moduleManager.container.moduleContainerService.saveModuleStates(pageName, settingsToSave);
            }
        }

    }

    async applyFilterOnModule(moduleManager: ModuleManager) {
        const moduleContainer = moduleManager.container;
        try {
            const params = { waitBeforeSaving: null, settingsManager: this.settingsManager, fromModal: true };
            await moduleContainer.applySettings(params);
            moduleContainer['navigationService'].showOverlay = false;
            moduleContainer.settingsOpen = false;
        } catch (e) {
            console.error(e);
            this.toastIsError = true;
            this.showToast = true;
        }
    }

    /**
     * This function handles the removal of a filter and the silent save for that
     * checks the various cases for the apply button to be enabled/disabled
     * also updates the header component filter count
     * */
    async filterRemoved(){
        const activeDashboard = this.dashboardService.getActiveDashboard();
        let savedFilters = await this.dashboardService.getCxOneDashboardFilters(activeDashboard.DashboardKey);
        if (savedFilters !== "[]" || this.isApplied) {
            //1. I know there are saved filters so that means I need to do a comparison to see if the removed filter is one of the intialSelections
            await this.filterRemovedSilentSave();
        }

        //3. check if there is new or edits to the filters and do something with the apply button accordingly
        let edit = false;
        this.selectedFilters.selection.forEach(selectedFilter => {
            const initialFilter = this.initialSelection.find(filter => filter.filterId === selectedFilter.filterId);
            if (initialFilter && JSON.stringify(initialFilter.value) !== JSON.stringify(selectedFilter.value)) {
                edit = true;
            }
        });

        let differences = this.filterDifference(this.selectedFilters.selection, this.initialSelection);

        if ((edit || differences.length > 0) && this.selectedFilters.isValid === true) {
            this.applyButtonEnabled = true;
        }
        else{
            this.applyButtonEnabled = false;
        }
    }

    async filterRemovedSilentSave(){
        let differences = this.filterDifference(this.filterSelections.previousValue.selection, this.selectedFilters.selection);

        if(differences.length > 0) {
            let noMembersRemoved = false;
            const activeDashboard = this.dashboardService.getActiveDashboard();

            let silentSaveFilters = this.initialSelection.filter(filter => !differences.some(diff => diff.filterId === filter.filterId));

            // Check if any members were filtered out
            noMembersRemoved = silentSaveFilters.length === this.initialSelection.length;

            //2. if it is then I need to remove it from initialSelections and then silent save initial selections only
            if (!noMembersRemoved){
                this.isApplied = false;
                await this.dashboardService.setMainCxOneDashboardFilters(activeDashboard.DashboardKey, silentSaveFilters);
                this.updateFilterText.emit(silentSaveFilters.length === 0 ? null : silentSaveFilters.length);

                this.initialSelection = cloneDeep(silentSaveFilters);
            }
        }
    }

    private filterDifference(filters: any, comparisonFilters: any): any{
        let differences = [];

        filters.forEach(filter => {
            const matchingFilter = comparisonFilters.find(comparisonFilter => comparisonFilter.filterId === filter.filterId);
            if (!matchingFilter || JSON.stringify(matchingFilter.filterId) !== JSON.stringify(filter.filterId)) {
                differences.push(filter);
            }
        });

        return differences;
    }

    private revertSavedFilters() {
        if (this.filterSelections?.previousValue !== null && this.filterSelections?.previousValue !== undefined) {
            // Set current value to what is loaded from the endpoint.
            this.filterSelections.currentValue = { isValid: true, selection: [] }
            this.filterSelections.previousValue = undefined; // Set to undefined as when the component is initialized.
            this.onSelectionChange(this.selectedFilters); // Simulating a selection change to apply the values.
            this.filterSelections.previousValue = undefined; // Set to undefined as when the component is initialized.
            this.applyButtonEnabled = false; // Set as false to not trigger any confirmation message.
            this.isApplyButtonEnabled.emit(this.applyButtonEnabled); // Emit the event to set it in the dashboard-header.component.ts file.
            // Needed to force a reset of the component, if a reset is not executed the previously selected filters will not appear as selectable ones.
            this.initialized = false;
            setTimeout(() => { this.initialized = true; }, 0);
        }
    }

    standardizeLocalizationKey(key: string) {
        return ClearViewUtils.capitalizeWords(
            ClearViewUtils.removeSpecialCharacters(key.trim().toLowerCase())
        ).replaceAll(" ", "_");
    }

    // TODO - add back when new attribute flow is introduced
    /*async saveAttributes(moduleManager: ModuleManager) {
        const pageName = `${moduleManager.getModuleKey()}_temp`;
        let attributeModel = {
            Type: 0,
            SelectedCategories: [],
        };
        if (this.getSelectedFilterValue("filterMediaType")) {
            attributeModel.SelectedCategories.push({
                SelectedCategoryID: 935,
                SelectedAttributeIDs: this.getSelectedFilterValue("filterMediaType"),
            });
        }
        if (this.getSelectedFilterValue("filterDirection")) {
            attributeModel.SelectedCategories.push({
                SelectedCategoryID: 936,
                SelectedAttributeIDs: this.getSelectedFilterValue("filterDirection"),
            });
        }
        if (this.getSelectedFilterValue("filterChannels")) {
            attributeModel.SelectedCategories.push({
                SelectedCategoryID: 1012,
                SelectedAttributeIDs: this.getSelectedFilterValue("filterChannels"),
            });
        }
        this.settingsManager.dataAttributesModel = attributeModel;
        moduleManager.moduleSettings.dataAttributesModel = this.settingsManager.dataAttributesModel;
        moduleManager.moduleSettings.selectedGroupType = this.settingsManager.selectedToggle;

        const moduleSettingsManager = moduleManager.slideoutSettingsManager.getValue();
        if (moduleSettingsManager) {
            moduleSettingsManager.selectedLevelName = this.settingsManager.selectedLevelName;
            moduleSettingsManager.onAttributeReloaded.next(this.settingsManager.dataAttributesModel);
        }

        //if (attributeModel.SelectedCategories.length > 0) {
        //    await this.attributeSelectorService.saveSelectorModel(pageName, this.settingsManager.dataAttributesModel);
        //    await this.applyFilterOnModule(moduleManager);
        //}
    }*/
}
